// store/api/leadListFoldersApi.ts
import { LeadListFolder } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export interface CreateFolderInput {
  name: string;
  parent_id?: string;
}

export interface UpdateFolderInput {
  name?: string;
  parent_id?: string | null;
}

export const leadListFoldersApi = createApi({
  reducerPath: 'leadListFoldersApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['LeadListFolder'],
  endpoints: (builder) => ({
    getFolders: builder.query<LeadListFolder[], void>({
      query: () => '/lead-list-folders',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'LeadListFolder' as const,
                id,
              })),
              { type: 'LeadListFolder', id: 'LIST' },
            ]
          : [{ type: 'LeadListFolder', id: 'LIST' }],
    }),

    createFolder: builder.mutation<LeadListFolder, CreateFolderInput>({
      query: (input) => ({
        url: '/lead-list-folders',
        method: 'POST',
        body: input,
      }),
      invalidatesTags: [{ type: 'LeadListFolder', id: 'LIST' }],
    }),

    updateFolder: builder.mutation<
      LeadListFolder,
      { folderId: string; input: UpdateFolderInput }
    >({
      query: ({ folderId, input }) => ({
        url: `/lead-list-folders/${folderId}`,
        method: 'PUT',
        body: input,
      }),
      invalidatesTags: (_, __, { folderId }) => [
        { type: 'LeadListFolder', id: folderId },
        { type: 'LeadListFolder', id: 'LIST' },
      ],
    }),

    deleteFolder: builder.mutation<{ message: string; id: string }, string>({
      query: (folderId) => ({
        url: `/lead-list-folders/${folderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'LeadListFolder', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetFoldersQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} = leadListFoldersApi;
